import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, interval, mergeMap, Observable, startWith, Subscription } from 'rxjs';
import { catchError, filter, finalize, map, take, tap } from 'rxjs/operators';

import {
	DPortfolioSymbolData,
	DWatchSymbolData,
	MarketMapData,
	PortfolioSymbolData,
	returnChartData,
	StockConditionData,
	StockOrderData,
	SymbolDetailsHeader,
	SymbolDetailsIndInstTrade,
	SymbolDetailsInfo,
	SymbolDetailsMarketDepth,
	SymbolIndInst,
	TSEIndexDataItem,
	WatchSymbolData
} from '@easy-lib/modules/shared/models/market-data.model';
import { LsService } from '@easy-lib/modules/shared/services/Ls.service';
import { SymbolService } from '@easy-lib/modules/shared/services/symbol.service';

import { ISIN } from '@easy-lib/constants/isins';
import { TreeMapOptionSourceType } from '@easy-lib/modules/market-map';
import { defaultMarketData, ISymbolMarketData } from '@easy-lib/modules/shared/models';
import { ILsData, LsSchemaType } from '@easy-lib/modules/shared/models/ls.model';
import { LsSchemas } from '@easy-lib/modules/shared/models/lsSchemas';
import { BaseService } from '@easy-lib/modules/shared/services/Base/base.service';
import { activeStockLsData, getColorOfSymbolState } from '@easy-lib/tools';

@Injectable({
	providedIn: 'root'
})
export class MarketDataService extends BaseService {
	public lsService: LsService = inject(LsService);
	private symbolService: SymbolService = inject(SymbolService);
	indexes: TSEIndexDataItem[] = [];
	indexesSubject = new BehaviorSubject<TSEIndexDataItem[]>(this.indexes);
	indexes$ = this.indexesSubject.asObservable();

	lsDataReceivedIntervalSubs: { [key: string]: Subscription } = {};
	lsDataReceived = new Map([]);

	constructor() {
		super();
		this.subscribeToLsIndex();
		this.lsService.resetLsManually$.pipe(filter(res => res)).subscribe(() => {
			this.subscribeToLsIndex();
		});
	}

	getTseIndex(): Observable<TSEIndexDataItem[]> {
		return this.httpService
			.get<TSEIndexDataItem[]>(`${this.mtsPath + this.apiUrls.symbol}MarketData/tse-index`)
			.pipe(
				tap(data => {
					this.indexes = data;
					this.indexesSubject.next(data);
				})
			);
	}
	GetPortfolioData(isins: string[]): Observable<PortfolioSymbolData[]> {
		return this.httpService.post<PortfolioSymbolData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/portfolio`,
			{
				body: isins
			}
		);
	}
	GetRealtimePortfolioData(isins: string[]): Observable<DPortfolioSymbolData[]> {
		return this.httpService.post<DPortfolioSymbolData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/dPortfolio`,
			{
				body: isins
			}
		);
	}
	GetDPortfolioData(isins: string[]): Observable<DPortfolioSymbolData[]> {
		return this.httpService.post<DPortfolioSymbolData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/dPortfolio`,
			{
				body: isins
			}
		);
	}

	getReturnChartData(isin: string): Observable<returnChartData> {
		return this.httpService.post<returnChartData>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/return-chart-data`,
			{
				body: { isin }
			}
		);
	}

	GetStockInfo(isin: string): Observable<SymbolDetailsInfo> {
		return this.httpService.post<SymbolDetailsInfo>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/symbol-info-data`,
			{
				body: { isin }
			}
		);
	}

	GetWatchData(isins: string[]): Observable<WatchSymbolData[]> {
		return this.httpService.post<WatchSymbolData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/market-watch`,
			{
				body: isins
			}
		);
	}

	GetDWatchData(isins: string[]): Observable<DWatchSymbolData[]> {
		return this.httpService
			.post<DWatchSymbolData[]>(`${this.mtsPath + this.apiUrls.symbol}MarketData/dmarket-watch`, { body: isins })
			.pipe(
				map(data => {
					data.map(async item => {
						const symbol = await this.symbolService.getSymbol(item.symbolISIN);
						item.canBuy = symbol.canBuy;
						item.canSell = symbol.canSell;
						return item;
					});
					return data;
				})
			);
	}

	GetStockListConditionData(isins: string[]): Observable<StockConditionData[]> {
		return this.httpService.post<StockConditionData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/stock-condition-data`,
			{
				body: isins
			}
		);
	}

	GetSymbolDetailsHeader(isin: string): Observable<SymbolDetailsHeader> {
		return this.httpService.post<SymbolDetailsHeader>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/symbol-details`,
			{
				body: { isin }
			}
		);
	}

	GetSymbolDetailsMarketDepth(isin: string): Observable<SymbolDetailsMarketDepth> {
		return this.httpService.post<SymbolDetailsMarketDepth>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/market-depth`,
			{
				body: { isin }
			}
		);
	}

	GetSymbolDetailsIndInstTrade(isin: string): Observable<SymbolDetailsIndInstTrade> {
		return this.httpService.post<SymbolDetailsIndInstTrade>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/indInstTrade`,
			{
				body: { isin }
			}
		);
	}

	symbolIndInst(symbolIsin: string): Observable<SymbolIndInst> {
		const url = `${this.mtsPath}${this.apiUrls.easy}symbol-analysis/ind-inst?isin=${symbolIsin}`;
		// const url = `https://stage-api-mts.easytrader.ir/stgeasy/api/symbol-analysis/ind-inst?isin=${symbolIsin}`;
		return this.httpService.get<SymbolIndInst>(url).pipe(map(data => data[0]));
	}

	GetStockOrderData(isin: string): Observable<StockOrderData> {
		return this.httpService.post<StockOrderData>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/order-market-data`,
			{
				body: { isin }
			}
		);
	}

	getMarketMapData(time?: number): Observable<MarketMapData[]> {
		const marketMap$ = this.httpService.get<MarketMapData[]>(
			`${this.mtsPath + this.apiUrls.symbol}MarketData/market-map`
		);

		if (!time) return marketMap$;
		return interval(time)
			.pipe(startWith(0))
			.pipe(mergeMap(() => marketMap$));
	}
	getOptionMarketMapData(time?: number, sourceType?: TreeMapOptionSourceType): Observable<MarketMapData[]> {
		const soursName = sourceType === TreeMapOptionSourceType.BaseSymbol ? 'BaseSymbols' : 'Symbols';
		const marketMap$ = this.httpService.get<MarketMapData[]>(
			`${this.mtsPath + this.apiUrls.symbol}OptionMarketData/${soursName}/Map`
		);

		if (!time) return marketMap$;
		return interval(time)
			.pipe(startWith(0))
			.pipe(mergeMap(() => marketMap$));
	}

	subscribeToLsIndex() {
		this.lsService.subscribeToLs(LsSchemaType.Index, ISIN.code.TEDPIX).subscribe(data => {
			this.setIndexParameters(data);
		});
	}

	setIndexParameters(data: ILsData): void {
		let inx = -1;
		if (data.inst == ISIN.code.TEDPIX) {
			//بورس
			inx = this.indexes.findIndex(index => index.symbolIsin.toUpperCase() === ISIN.code.TEDPIX);
		} else if (data.inst == ISIN.code.IFX) {
			//فرابورس
			inx = this.indexes.findIndex(index => index.symbolIsin.toUpperCase() === ISIN.code.IFX);
		}
		if (inx < 0) {
			this.indexes.push({
				symbolIsin: data.inst,
				dayOfEvent: new Date(),
				indexChanges: 0,
				lastIndexValue: 0,
				symbolTitle: '',
				percentVariation: 0,
				yesterdayPrice: 0
			});
			inx = this.indexes.length - 1;
		}

		switch (data.name) {
			case LsSchemas.IndexSchema.YesterdayPrice:
				this.indexes[inx].yesterdayPrice = parseFloat(data.val);
				this.indexes[inx].indexChanges = this.indexes[inx].lastIndexValue - this.indexes[inx].yesterdayPrice;
				break;
			case LsSchemas.IndexSchema.LastIndexValue:
				this.indexes[inx].lastIndexValue = parseFloat(data.val);
				this.indexes[inx].indexChanges = this.indexes[inx].lastIndexValue - this.indexes[inx].yesterdayPrice;

				break;
			case LsSchemas.IndexSchema.PercentVariation:
				this.indexes[inx].percentVariation = parseFloat(data.val) / 100;
				break;
			case LsSchemas.IndexSchema.DayOfEvent:
				this.indexes[inx].dayOfEvent = new Date(
					data.val.trim().substring(0, 4),
					data.val.trim().substring(4, 6) - 1,
					data.val.trim().substring(6, 8)
				);
				break;
		}
		this.indexesSubject.next(this.indexes);
	}

	isIndexForToday(): boolean {
		if (!this.indexes[ISIN.code.TEDPIX].dayOfEvent) return false;
		const today = new Date();
		return this.indexes[ISIN.code.TEDPIX].dayOfEvent.toDateString() === today.toDateString();
	}

	getMehodName(method: any) {
		const str = method.toString();
		return str
			.replace(/[^a-zA-Z]/gm, '')
			.replace('this', '')
			.replace('function', '')
			.replace('return', '');
	}

	callMarketDataIfLsNotConnected(marketDataCall: () => void, intervalMs = 2000) {
		const key = this.getMehodName(marketDataCall);
		this.lsDataReceived.set(key, false);
		if (this.lsDataReceivedIntervalSubs[key]) this.lsDataReceivedIntervalSubs[key].unsubscribe();

		this.lsDataReceivedIntervalSubs[key] = interval(intervalMs)
			.pipe(
				take(1),
				tap(() => {
					if (!this.lsDataReceived.get(key)) {
						marketDataCall();
					}
				})
			)
			.subscribe();
	}

	lsDataIsReceived(name: string) {
		let methodName = this.getMehodName(name);
		for (const [key] of this.lsDataReceived.entries()) {
			if ((key as string).includes(methodName) || (methodName as string).includes(key as string)) {
				methodName = key;
			}
		}

		this.lsDataReceived.set(methodName, true);
	}
}

export class MarketDataFlow {
	private subId: string;
	marketDataService: MarketDataService;
	lsService: LsService;
	symbolIsin: string;
	public marketData = new BehaviorSubject<ISymbolMarketData>({ ...defaultMarketData });
	public marketData$ = this.marketData.asObservable();

	constructor(marketDataService: MarketDataService, lsService: LsService, symbolIsin: string) {
		this.marketDataService = marketDataService;
		this.lsService = lsService;
		this.symbolIsin = symbolIsin;
	}
	public initFlow() {
		this.marketDataService.callMarketDataIfLsNotConnected(() => this.getStockDetailsMarketData());
		this.lsService.subscribeToLs(LsSchemaType.ActiveStock, this.symbolIsin).subscribe(data => {
			if (data.inst === this.symbolIsin) {
				if (!this.subId) this.subId = data.subId;
				this.marketDataService.lsDataIsReceived(this.getStockDetailsMarketData.name);
				const marketData: ISymbolMarketData = activeStockLsData(data.val);
				marketData.closingPriceVarPercent =
					this.calculateClosingPriceVarPercent(
						marketData.closingPrice,
						marketData.feeOfPreviousDaysClosingPrice,
						marketData.totalNumberOfSharesTraded
					) || 0;
				this.marketData.next(marketData as ISymbolMarketData);
			}
		});
	}

	getStockDetailsMarketData(): void {
		const sub = this.marketDataService
			.GetSymbolDetailsHeader(this.symbolIsin)
			.pipe(
				tap(data => {
					const marketData = this.marketData.value ?? defaultMarketData;
					marketData.lastTradedPrice = data.lastTradedPrice;
					marketData.feeOfPreviousDaysClosingPrice = data.feeOfPreviousDaysClosingPrice;
					marketData.lastTradedPriceVar = this.calculateLastTradedPriceVar(
						data.lastTradedPrice,
						data.feeOfPreviousDaysClosingPrice
					);
					marketData.lastTradedPriceVarPercent = data.priceVar;
					marketData.stateColor = getColorOfSymbolState(data.stateCode);
					marketData.closingPrice = data.closingPrice;
					marketData.closingPriceVarPercent = this.calculateClosingPriceVarPercent(
						data.closingPrice,
						data.feeOfPreviousDaysClosingPrice,
						data.totalNumberOfSharesTraded
					);
					marketData.totalNumberOfSharesTraded = data.totalNumberOfSharesTraded;
					marketData.stateCode = data.stateCode;

					this.marketData.next(marketData as ISymbolMarketData);
				}),
				catchError((error, caught) => caught),
				finalize(() => sub.unsubscribe())
			)
			.subscribe();
	}

	calculateLastTradedPriceVar(lastTradedPrice?: number, feeOfPreviousDaysClosingPrice?: number): number {
		return lastTradedPrice - (feeOfPreviousDaysClosingPrice ?? 0);
	}

	calculateClosingPriceVarPercent(
		closePrice?: number,
		feeOfPreviousDaysClosingPrice?: number,
		totalNumberOfSharesTraded?: number
	): number {
		if (
			feeOfPreviousDaysClosingPrice === 0 ||
			!feeOfPreviousDaysClosingPrice ||
			!totalNumberOfSharesTraded ||
			totalNumberOfSharesTraded === 0
		)
			return 0;

		if (closePrice > 0 && feeOfPreviousDaysClosingPrice > 0)
			return (
				Math.round(((closePrice - feeOfPreviousDaysClosingPrice) / feeOfPreviousDaysClosingPrice) * 10000) / 100
			);
		return 0;
	}

	destroy() {
		this.lsService.unsubscribeSchemaById(this.subId);
	}
}
